/**
 * Terms of Use — approved copy supplied by Chrissa Valanti on 20 June 2026.
 * No changes other than the body content swap. Mirrors the Privacy page structure
 * for visual consistency.
 */
import { Link } from "react-router-dom";
import { useSEO, SEO } from "@/lib/seo";

const MAIL = "chrissavalanti@gmail.com";

function MailLink({ children }) {
    return (
        <a
            href={`mailto:${MAIL}`}
            className="text-oxblood underline-offset-4 hover:underline transition-colors"
        >
            {children || MAIL}
        </a>
    );
}

function H2({ n, children }) {
    return (
        <h2 className="font-serif text-2xl md:text-3xl leading-snug text-charcoal mt-16 mb-6">
            <span className="text-oxblood/70 mr-3">{n}.</span>
            {children}
        </h2>
    );
}

function P({ children }) {
    return <p className="body-base text-charcoal/80">{children}</p>;
}

function UL({ children }) {
    return (
        <ul className="body-base text-charcoal/80 list-disc pl-6 marker:text-oxblood/60 space-y-2">
            {children}
        </ul>
    );
}

export default function Terms() {
    useSEO(SEO.terms);
    return (
        <main className="bg-ivory text-charcoal">
            <section className="pt-36 md:pt-44 pb-10 md:pb-14">
                <div className="container-narrow">
                    <p className="eyebrow text-oxblood">Terms</p>
                    <h1 className="display-2 mt-6">Terms of Use.</h1>
                    <p className="mt-6 eyebrow text-stone">Effective date: 20 June 2026</p>
                </div>
            </section>

            <section className="pb-24 md:pb-32">
                <div className="container-narrow space-y-5">
                    <P>
                        These Terms of Use apply to the Chrissa Valanti personal website available at:
                    </P>
                    <UL>
                        <li>chrissavalanti.com</li>
                        <li>chrissavalanti.com.au</li>
                    </UL>
                    <P>The website is operated by Chrissa Valanti.</P>
                    <P>By accessing or using the website, you agree to these Terms of Use.</P>

                    {/* 1 */}
                    <H2 n="1">Purpose of the website</H2>
                    <P>This website shares:</P>
                    <UL>
                        <li>personal stories and reflections;</li>
                        <li>information about Chrissa Valanti&apos;s journey;</li>
                        <li>information about music and speaking;</li>
                        <li>information about Valanti Labs;</li>
                        <li>information about ALATI;</li>
                        <li>journal and editorial material; and</li>
                        <li>opportunities to make contact or enquire about collaborations.</li>
                    </UL>
                    <P>
                        The website is provided for general information, storytelling and communication
                        purposes.
                    </P>

                    {/* 2 */}
                    <H2 n="2">No medical or professional advice</H2>
                    <P>
                        The website contains personal accounts relating to illness, treatment, recovery,
                        movement, wellbeing and adversity.
                    </P>
                    <P>This content:</P>
                    <UL>
                        <li>reflects personal experience only;</li>
                        <li>is not medical advice;</li>
                        <li>is not psychological, therapeutic, legal, financial or other professional advice;</li>
                        <li>should not be relied upon to diagnose, treat or manage a health condition; and</li>
                        <li>does not replace advice from a qualified professional who understands your circumstances.</li>
                    </UL>
                    <P>
                        Always seek appropriate professional advice before making decisions about your health,
                        treatment, medication, exercise, movement or wellbeing.
                    </P>
                    <P>
                        Nothing on this website creates a doctor–patient, therapist–client, coach–client or
                        other professional relationship.
                    </P>
                    <P>
                        In an emergency, contact the appropriate emergency service or qualified healthcare
                        provider.
                    </P>

                    {/* 3 */}
                    <H2 n="3">Valanti Labs</H2>
                    <P>Information about Valanti Labs is general and introductory.</P>
                    <P>Unless expressly stated otherwise:</P>
                    <UL>
                        <li>it does not represent the public release of a medical device;</li>
                        <li>it does not constitute medical assessment, diagnosis or treatment;</li>
                        <li>it is not a promise that a particular product or feature is available;</li>
                        <li>it is not an invitation to disregard professional medical advice; and</li>
                        <li>it does not guarantee any particular movement, rehabilitation or health outcome.</li>
                    </UL>
                    <P>Features, research, development plans and availability may change.</P>

                    {/* 4 */}
                    <H2 n="4">ALATI</H2>
                    <P>
                        Information about ALATI is general and may describe products, ingredients,
                        craftsmanship and the story behind the business.
                    </P>
                    <P>
                        Unless a separate online shop or sales agreement is introduced, this website does not
                        currently process ALATI purchases, payments, delivery orders or product returns.
                    </P>
                    <P>
                        Any future purchase will be governed by the terms provided at the point of sale.
                    </P>
                    <P>
                        Information about soap or skincare products is not medical advice. Individual
                        sensitivities and allergies can vary.
                    </P>

                    {/* 5 */}
                    <H2 n="5">Music, speaking and media enquiries</H2>
                    <P>
                        Information about music, speaking, interviews, media appearances and partnerships
                        does not create a booking or contractual commitment.
                    </P>
                    <P>
                        A booking, appearance, licence, partnership or collaboration exists only when the
                        relevant parties agree to separate written terms.
                    </P>

                    {/* 6 */}
                    <H2 n="6">Personal stories</H2>
                    <P>
                        Personal stories are shared honestly from the perspective and recollection of the
                        person telling them.
                    </P>
                    <P>
                        Some details may be shortened, withheld or expressed generally to protect privacy,
                        dignity and the rights of other people.
                    </P>
                    <P>
                        No person should assume that every private, medical or family detail has been
                        published.
                    </P>

                    {/* 7 */}
                    <H2 n="7">Intellectual property</H2>
                    <P>
                        Unless otherwise stated, the website and its original content are owned by or
                        licensed to Chrissa Valanti.
                    </P>
                    <P>This includes:</P>
                    <UL>
                        <li>written content;</li>
                        <li>personal stories;</li>
                        <li>photographs;</li>
                        <li>branding;</li>
                        <li>graphics;</li>
                        <li>website design;</li>
                        <li>audio and video material;</li>
                        <li>downloadable materials; and</li>
                        <li>the arrangement of website content.</li>
                    </UL>
                    <P>
                        You may access and share links to publicly available pages for personal and
                        non-commercial purposes.
                    </P>
                    <P>You must not, without prior written permission:</P>
                    <UL>
                        <li>reproduce substantial website content;</li>
                        <li>republish photographs or personal stories;</li>
                        <li>use content for commercial purposes;</li>
                        <li>alter or misrepresent the content;</li>
                        <li>remove copyright or ownership notices;</li>
                        <li>present the content as your own;</li>
                        <li>train, develop or promote a commercial product using the content; or</li>
                        <li>
                            use the name, image or likeness of Chrissa Valanti in a way that suggests
                            endorsement, affiliation or approval.
                        </li>
                    </UL>
                    <P>
                        Legitimate quotation, review, reporting and other uses permitted by law are not
                        restricted.
                    </P>

                    {/* 8 */}
                    <H2 n="8">Information you submit</H2>
                    <P>When you submit an enquiry, you confirm that:</P>
                    <UL>
                        <li>the information is accurate to the best of your knowledge;</li>
                        <li>you are entitled to provide it;</li>
                        <li>it does not unlawfully infringe another person&apos;s rights;</li>
                        <li>it does not contain malicious code or unlawful content; and</li>
                        <li>you are not using the website to harass, deceive or harm another person.</li>
                    </UL>
                    <P>You retain ownership of your message.</P>
                    <P>
                        You give permission for the information to be used and shared as reasonably necessary
                        to review and respond to your enquiry.
                    </P>
                    <P>
                        Submitting an enquiry does not create a confidential, advisory, employment,
                        partnership or contractual relationship.
                    </P>
                    <P>
                        Do not submit information that is highly sensitive or subject to confidentiality
                        obligations unless specifically requested through an appropriate secure process.
                    </P>

                    {/* 9 */}
                    <H2 n="9">Acceptable use</H2>
                    <P>You must not:</P>
                    <UL>
                        <li>interfere with the website&apos;s operation or security;</li>
                        <li>attempt to access systems or data without authorisation;</li>
                        <li>submit spam, automated messages or fraudulent enquiries;</li>
                        <li>introduce malware or harmful code;</li>
                        <li>scrape or harvest personal information;</li>
                        <li>impersonate another person;</li>
                        <li>use the website unlawfully; or</li>
                        <li>use the website in a way that could damage its reputation, availability or security.</li>
                    </UL>
                    <P>Access may be restricted where misuse is suspected.</P>

                    {/* 10 */}
                    <H2 n="10">Accuracy and availability</H2>
                    <P>
                        Reasonable care is taken when preparing website content, but the website may contain
                        errors, omissions or information that becomes outdated.
                    </P>
                    <P>No guarantee is given that:</P>
                    <UL>
                        <li>all content is complete or current;</li>
                        <li>the website will always be available;</li>
                        <li>the website will operate without interruption or error;</li>
                        <li>external links will remain available; or</li>
                        <li>
                            future projects, products or services described on the website will proceed exactly
                            as discussed.
                        </li>
                    </UL>
                    <P>Content may be corrected, updated, removed or changed without notice.</P>

                    {/* 11 */}
                    <H2 n="11">External websites</H2>
                    <P>
                        The website may contain links to third-party websites, platforms, music services or
                        social-media pages.
                    </P>
                    <P>
                        These links are provided for convenience and do not necessarily represent endorsement.
                    </P>
                    <P>
                        Chrissa Valanti does not control and is not responsible for third-party content,
                        security, availability, terms or privacy practices.
                    </P>
                    <P>You access external services at your own discretion.</P>

                    {/* 12 */}
                    <H2 n="12">Limitation of liability</H2>
                    <P>
                        To the maximum extent permitted by law, Chrissa Valanti is not responsible for loss
                        arising from:
                    </P>
                    <UL>
                        <li>reliance on general website content;</li>
                        <li>decisions made without appropriate professional advice;</li>
                        <li>interruption or unavailability of the website;</li>
                        <li>unauthorised access caused by circumstances beyond reasonable control;</li>
                        <li>third-party websites or services; or</li>
                        <li>the use or misuse of information published on the website.</li>
                    </UL>
                    <P>
                        Nothing in these Terms excludes, restricts or modifies any right, guarantee, warranty
                        or remedy that cannot lawfully be excluded under the Australian Consumer Law or
                        another applicable law.
                    </P>

                    {/* 13 */}
                    <H2 n="13">Privacy</H2>
                    <P>
                        Personal information submitted through the website is handled in accordance with the
                        website&apos;s{" "}
                        <Link
                            to="/privacy"
                            className="text-oxblood underline-offset-4 hover:underline transition-colors"
                        >
                            Privacy Policy
                        </Link>
                        .
                    </P>

                    {/* 14 */}
                    <H2 n="14">Changes to these Terms</H2>
                    <P>
                        These Terms may be updated when the website, its services or applicable laws change.
                    </P>
                    <P>The current version will be published on this page with its effective date.</P>
                    <P>
                        Your continued use of the website after an update means the updated Terms apply to
                        future use.
                    </P>

                    {/* 15 */}
                    <H2 n="15">Governing law</H2>
                    <P>
                        These Terms are governed by the laws of the Northern Territory of Australia and
                        applicable Commonwealth laws.
                    </P>
                    <P>
                        You submit to the jurisdiction of the courts and tribunals having authority in the
                        Northern Territory of Australia.
                    </P>

                    {/* 16 */}
                    <H2 n="16">Contact</H2>
                    <P>Questions about these Terms may be directed to:</P>
                    <P>
                        <strong className="text-charcoal font-medium">Chrissa Valanti</strong>
                        <br />
                        <strong className="text-charcoal font-medium">Email:</strong> <MailLink />
                    </P>
                </div>
            </section>
        </main>
    );
}
